// Màn hình "Tiêu chuẩn đón tiếp" (TCĐT) — vi phạm tiêu chuẩn đón khách tại
// showroom từ ops-quality: theo cửa hàng → tiêu chí → trend tháng → trạng thái
// giải trình → chi tiết phiếu. Hiển thị cả phiếu confirmed lẫn pending (từ
// T5/2026 phiếu camera AI sync mang trạng thái pending). View-only.
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import { ChartBox, chartDataset } from '../components/ChartBox';
import type { OpsTcdtResponse } from '../types';
import { asNumber, money, monthLabel, num, reviewStatusLabel, reviewStatusTone, uniq } from '../utils';

interface OpsTcdtScreenProps {
  data: OpsTcdtResponse | null;
  loaded: boolean;
  busy: boolean;
  month: string;
  onMonth: (month: string) => void;
}

export function OpsTcdtScreen({ data, loaded, busy, month, onMonth }: OpsTcdtScreenProps) {
  if (busy && !loaded) return <div className="card empty">Đang tải dữ liệu tiêu chuẩn đón tiếp...</div>;
  if (!loaded) return <div className="card empty">Mở mục Tiêu chuẩn đón tiếp để tải dữ liệu.</div>;

  const months = data?.meta?.months || [];
  const byStore = data?.byStore || [];
  const byCriteria = data?.byCriteria || [];
  const trend = data?.trend || [];
  const explanations = data?.explanations || [];
  const details = data?.details || [];

  const total = byStore.reduce((a, r) => a + asNumber(r.cnt), 0);
  const confirmed = byStore.reduce((a, r) => a + asNumber(r.confirmed), 0);
  const pending = byStore.reduce((a, r) => a + asNumber(r.pending), 0);
  const fine = byStore.reduce((a, r) => a + asNumber(r.fine), 0);
  const explTotal = explanations.reduce((a, r) => a + asNumber(r.cnt), 0);
  const explApproved = explanations.filter(r => r.review_status === 'approved').reduce((a, r) => a + asNumber(r.cnt), 0);
  const explPending = explanations.filter(r => r.review_status === 'pending').reduce((a, r) => a + asNumber(r.cnt), 0);

  const explByStore = (code: string, status: string) =>
    explanations.filter(r => r.store_code === code && r.review_status === status).reduce((a, r) => a + asNumber(r.cnt), 0);

  // Trend tháng: cột tổng vi phạm (tách confirmed) + đường tiền phạt (triệu).
  const trendSets = [
    { ...chartDataset('Đã xác nhận', trend.map(r => asNumber(r.confirmed)), '#d71920'), stack: 'v' },
    { ...chartDataset('Chờ xác nhận', trend.map(r => asNumber(r.cnt) - asNumber(r.confirmed)), '#d97706'), stack: 'v' },
    chartDataset('Tiền phạt (triệu)', trend.map(r => Math.round(asNumber(r.fine) / 100000) / 10), '#7c3aed', 'line', 'y1'),
  ];

  // Top tiêu chí: horizontal bar.
  const topCriteria = byCriteria.slice(0, 10);
  const criteriaSets = [chartDataset('Số vi phạm', topCriteria.map(r => asNumber(r.cnt)), '#2563eb')];

  return (
    <>
      <section className="filters">
        <div className="actions start">
          <span className="kpi-label">Tháng</span>
          {months.map(m => (
            <button key={m} className={`tab ${month === m ? 'active' : ''}`} onClick={() => onMonth(m)}>{monthLabel(m)}</button>
          ))}
        </div>
      </section>

      <section className="grid kpis">
        <KpiCard label="Vi phạm TCĐT" value={num(total)} sub={`${monthLabel(month)} · gồm camera AI + CS ghi nhận`} tone={total ? 'red' : 'green'} />
        <KpiCard label="Đã xác nhận" value={num(confirmed)} sub={`${num(pending)} chờ xác nhận`} tone={confirmed ? 'red' : 'green'} />
        <KpiCard label="Tiền phạt" value={money(fine)} sub="100.000đ / vi phạm" tone={fine ? 'amber' : 'green'} />
        <KpiCard label="Giải trình" value={num(explTotal)} sub={`${num(explApproved)} duyệt · ${num(explPending)} chờ`} tone={explPending ? 'amber' : 'blue'} />
      </section>

      <section className="grid split atop space">
        <div className="card">
          <div className="section-title">
            <h2>Trend vi phạm theo tháng</h2>
            <span>Cột = số vi phạm (xác nhận / chờ) · đường = tiền phạt</span>
          </div>
          <div className="chart"><ChartBox labels={trend.map(r => monthLabel(r.ym))} datasets={trendSets} options={{ scales: { x: { stacked: true, grid: { display: false } }, y: { stacked: true, beginAtZero: true }, y1: { position: 'right', beginAtZero: true, grid: { display: false } } } }} /></div>
        </div>
        <div className="card">
          <div className="section-title">
            <h2>Top tiêu chí vi phạm · {monthLabel(month)}</h2>
            <span>{num(byCriteria.length)} tiêu chí có vi phạm</span>
          </div>
          {topCriteria.length === 0 ? (
            <div className="card empty">Không có vi phạm trong {monthLabel(month)}.</div>
          ) : (
            <div className="chart"><ChartBox labels={topCriteria.map(r => `${r.criteria_code} ${String(r.criteria_name || '').slice(0, 28)}`)} datasets={criteriaSets} options={{ indexAxis: 'y', scales: { x: { beginAtZero: true }, y: { grid: { display: false } } } }} /></div>
          )}
        </div>
      </section>

      <section className="card space">
        <div className="section-title">
          <h2>Theo cửa hàng · {monthLabel(month)}</h2>
          <span>Vi phạm + tiền phạt + tiến độ giải trình</span>
        </div>
        <div className="scroll">
          <table>
            <thead><tr>
              <th>Cửa hàng</th><th className="right">Vi phạm</th><th className="right">Xác nhận</th><th className="right">Chờ</th>
              <th className="right">Tiền phạt</th><th className="right">Giải trình duyệt</th><th className="right">Chờ duyệt</th><th className="right">Từ chối</th>
            </tr></thead>
            <tbody>{byStore.map(r => (
              <tr key={r.store_code}>
                <td><b>{r.store_code}</b></td>
                <td className="right tnum"><b>{num(r.cnt)}</b></td>
                <td className="right tnum">{asNumber(r.confirmed) ? <Tag tone="red">{num(r.confirmed)}</Tag> : <span className="muted">0</span>}</td>
                <td className="right tnum">{asNumber(r.pending) ? <Tag tone="amber">{num(r.pending)}</Tag> : <span className="muted">0</span>}</td>
                <td className="right tnum">{money(r.fine)}</td>
                <td className="right tnum">{num(explByStore(r.store_code, 'approved'))}</td>
                <td className="right tnum">{num(explByStore(r.store_code, 'pending'))}</td>
                <td className="right tnum muted">{num(explByStore(r.store_code, 'rejected'))}</td>
              </tr>
            ))}
            {byStore.length === 0 && <tr><td colSpan={8} className="empty">Không có vi phạm trong {monthLabel(month)}.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <div className="section-title">
          <h2>Chi tiết phiếu vi phạm · {monthLabel(month)}</h2>
          <span>{num(details.length)} phiếu gần nhất · trạng thái giải trình theo từng phiếu</span>
        </div>
        {details.length === 0 ? (
          <div className="card empty">Không có phiếu vi phạm trong {monthLabel(month)}.</div>
        ) : (
          <div className="scroll tall">
            <table>
              <thead><tr>
                <th>Thời điểm</th><th>Cửa hàng</th><th>Tiêu chí</th><th className="right">Tiền phạt</th>
                <th>Trạng thái phiếu</th><th>Giải trình</th>
              </tr></thead>
              <tbody>{details.map(r => (
                <tr key={r.id}>
                  <td className="tnum muted">{r.at || r.day}</td>
                  <td><b>{r.store_code}</b></td>
                  <td className="need"><b>{r.criteria_code}</b> {r.criteria_name || ''}</td>
                  <td className="right tnum">{money(r.fine_amount)}</td>
                  <td><Tag tone={r.status === 'confirmed' ? 'red' : 'amber'}>{r.status === 'confirmed' ? 'Xác nhận' : 'Chờ xác nhận'}</Tag></td>
                  <td><Tag tone={reviewStatusTone(r.expl_status)}>{reviewStatusLabel(r.expl_status)}</Tag></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
